import { useReactTable } from '@tanstack/react-table';
import isNil from 'lodash/isNil';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { match } from 'ts-pattern';

import { MedicalCodeTag } from '@eluve/blocks';
import {
  Card,
  ColDefBuilder,
  EluveCardContent,
  EluveCardHeader,
  P,
  ServerDataTable,
  TableSearch,
  VStack,
  legacyTextStyles,
  useDataTableForQuery,
} from '@eluve/components';
import {
  SearchableBillingCodes,
  useSearchAllMedicalCodes,
} from '@eluve/frontend-feature-appointment';
import {
  MedicalCodeTypesEnum,
  ProceduralMedicalCodeTypes,
} from '@eluve/graphql-types';
import {
  DiagnosticMedicalCodeTypes,
  UserGroupCodeConfigsOrderBy,
} from '@eluve/graphql-types';
import { ResultOf } from '@eluve/graphql.tada';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';
import { useDialog } from '@eluve/utility-hooks';

import { AddUserGroupConfigModal } from './AddUserGroupConfigModal';
import { DeleteUserGroupCodeConfigCell } from './DeleteUserGroupCodeConfigCell';
import { DownloadCodesButton } from './DownloadCodesButtons';
import { UploadCodesButton } from './UploadCodesButton';
import { searchCodesQuery } from './user-group-configs.operations';

type Row = {
  tenantId: string;
  userGroupId: string;
  configId: string;
  code: string;
  codeType: MedicalCodeTypesEnum;
  label: string;
  price: number | null;
  modifiers: string[];
};

const cols = new ColDefBuilder<Row>()
  .defaultSortable('codeType', {
    label: 'Code Type',
    cellRenderer: (row) => {
      return row.codeType.replace('_', ' ');
    },
  })
  .defaultSortable('code', {
    cellRenderer: (row) => (
      <div className="ml-2">
        <MedicalCodeTag code={row.code} codeType={row.codeType} />
      </div>
    ),
  })
  .defaultSortable('label')
  .defaultSortable('price', {
    cellRenderer: (row) => {
      return row.price ? `$${row.price}` : '-';
    },
  })
  .defaultSortable('modifiers', {
    cellRenderer: (row) => {
      return ProceduralMedicalCodeTypes.includes(row.codeType)
        ? row.modifiers.join(', ')
        : '-';
    },
  })
  .colDef({
    accessorKey: 'actions',
    header: '',
    cell: (row) => (
      <DeleteUserGroupCodeConfigCell
        tenantId={row.row.original.tenantId}
        configId={row.row.original.configId}
      />
    ),
  })
  .build();

type SelectedCode = {
  id: string;
  code: string;
  codeType: MedicalCodeTypesEnum;
  description: string | null;
} | null;

export const UserGroupCodeConfigs: React.FC<{}> = () => {
  const tenantId = useAssignedTenantIdFromParams();
  const { userGroupId } = useParams() as { userGroupId: string };

  const convertToRows = (results: ResultOf<typeof searchCodesQuery>): Row[] => {
    return (results.userGroupsByPk?.configs ?? []).map<Row>((c) => {
      return {
        tenantId,
        userGroupId,
        configId: c.id,
        label: c.label ?? '',
        code: c.medical_code.code,
        codeType: c.medical_code.codeType,
        price:
          DiagnosticMedicalCodeTypes.includes(c.medical_code.codeType) ||
          isNil(c.price)
            ? null
            : c.price / 100,
        modifiers: (c.modifiers as string[]) ?? [],
      };
    });
  };

  const {
    data,
    rows,
    reactTableOptions,
    setSearch,
    search,
    isPending,
    refetch,
  } = useDataTableForQuery({
    query: searchCodesQuery,
    convertToRows,
    convertSearchParamsToVariables: ({ search, limit, offset, sorting }) => ({
      tenantId,
      userGroupId,
      searchTerm: search ? `%${search}%` : '%',
      limit,
      offset,
      orderBy:
        sorting && sorting.length
          ? sorting.map(({ desc, id }) => ({
              ...match<keyof Row, UserGroupCodeConfigsOrderBy>(id)
                .with('code', () => ({
                  medical_code: {
                    code: desc ? 'DESC_NULLS_LAST' : 'ASC',
                  },
                }))
                .with('codeType', () => ({
                  medical_code: {
                    codeType: desc ? 'DESC' : 'ASC',
                  },
                }))
                .otherwise(() => ({
                  [id]: desc ? 'DESC_NULLS_LAST' : 'ASC',
                })),
            }))
          : null,
    }),
  });

  const rowCount = data?.userGroupCodeConfigsAggregate?.aggregate?.count ?? 0;

  const table = useReactTable({
    data: rows,
    columns: cols,
    rowCount,
    ...reactTableOptions,
  });

  // Search bar for adding individual codes
  const dialog = useDialog();
  const [selectedCode, setSelectedCode] = useState<SelectedCode>(null);

  const searchMedicalCodes = useSearchAllMedicalCodes({});

  return (
    <Card>
      <EluveCardHeader>
        <P>Medical Code Configurations</P>
      </EluveCardHeader>
      <EluveCardContent>
        <VStack
          wFull
          justify="between"
          align="start"
          className="sm:flex-row sm:align-middle"
        >
          <p className={`${legacyTextStyles.body({ size: 'l' })} text-nowrap`}>
            Bulk Actions
          </p>

          <VStack gap={3} wFull justify="end" className="sm:flex-row">
            <DownloadCodesButton hasEnabledCodes={rows.length > 0} />
            <UploadCodesButton onSuccess={() => refetch()} />
          </VStack>
        </VStack>

        <VStack gap={3}>
          <p className={legacyTextStyles.body({ size: 'l' })}>
            Add Individual Codes
          </p>
          <SearchableBillingCodes
            searchCodes={searchMedicalCodes}
            onCodeAdded={(code) => {
              setSelectedCode(code);
              dialog.openDialog();
            }}
            selectedCodes={[]}
            shouldRenderValue={false}
          />
        </VStack>

        <AddUserGroupConfigModal
          userGroupId={userGroupId}
          dialog={dialog}
          code={selectedCode}
          onSuccess={() => refetch()}
        />

        <VStack gap={3}>
          <p className={legacyTextStyles.body({ size: 'l' })}>Medical Codes</p>
          <TableSearch value={search ?? ''} onChange={setSearch} />
          <ServerDataTable<Row> table={table} isPending={isPending} />
        </VStack>
      </EluveCardContent>
    </Card>
  );
};
